import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { getLocale } from "@arcgis/core/intl";
import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";

import PublishAppViewModel from "../../data/PublishApp/PublishAppViewModel";
import { isMobileView } from "../../utils/appUtils";
import {
  AppTypeInfo,
  EResourceType,
  previewType,
  ResourceInfo,
} from "../AppTypeTile/AppTypeTile";
import "./styles/IFrameAppPreview.scss";
import PublishForm from "../PublishForm/PublishForm";

import { focusElement } from "../../utils/focusUtils";
import { applyShadowStyle } from "../../utils/utils";
import { SHADOW_STYLE_PREVIEW_MODAL } from "../../utils/shadowStyles";
import { findEnvironmentByUrl } from "../../utils/urlUtils";

export interface IFrameAppPreviewProperties extends __esri.WidgetProperties {
  appTypeInfo: AppTypeInfo;
  resourceInfo: ResourceInfo;
  publishAppVM: PublishAppViewModel;
  previewType: previewType;
  existingApp?: __esri.PortalItem;
  sendFocusOnClose: Function;
}

@subclass("Landing.widgets.IFrameAppPreview")
export default class IFrameAppPreview extends Widget {
  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  resourceInfo: ResourceInfo;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  previewType: previewType;

  @property()
  existingApp: __esri.PortalItem;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  publishOverlay: PublishForm;

  @property()
  sendFocusOnClose: Function;

  @property()
  createButton: HTMLCalciteButtonElement;

  @property()
  isOverlayShowing: boolean = true;
  private _finalUrl: string;

  constructor(properties?: IFrameAppPreviewProperties) {
    super(properties);

    const rootModal = document.getElementById("rootModal");
    rootModal.classList.remove("rootModal--closed");
  }

  postInitialize() {
    this._decidePreviewUrl();

    this.watch("isOverlayShowing", () => {
      this.scheduleRender();
    });

    this.publishAppVM.navbarVM.closeLearnMorePopover();
  }

  render() {
    const { appPreviewModal, previewButtons } = this.appMessages;
    return (
      <div>
        <calcite-dialog
          class="iframe-modal"
          afterCreate={(modal: HTMLCalciteDialogElement) => {
            modal.setFocus();
            applyShadowStyle(SHADOW_STYLE_PREVIEW_MODAL, modal);
            this._handleModalClose(modal);
          }}
          open={true}
          placement="cover"
          modal={true}
          fullscreen="true"
          kind="info"
          scale="l"
          width-scale="l"
          messageOverrides={{ close: appPreviewModal.close }}
        >
          <h2 slot="header-content" class="iframe-modal__header">
            {previewButtons[this.previewType]} - {this.appTypeInfo.appTypeTitle}
          </h2>

          <div class="iframe-modal__app-preview-div">
            <iframe
              bind={this}
              class="iframe-app-preview"
              data-embed-type="instant-gallery"
              src={this._finalUrl}
              width="100%"
              height="100%"
              frameborder="0"
            />

            <div class="publish-overlay">{this.publishOverlay?.render()}</div>
          </div>
          <calcite-button
            class="preview-panel-close"
            slot="footer-end"
            scale="l"
            kind="neutral"
            appearance="solid"
            onclick={this.close.bind(this)}
          >
            {appPreviewModal.close}
          </calcite-button>
          {
            isMobileView() ? null :
            <calcite-button
              bind={this}
              key={`createButton-${this?.appTypeInfo?.appTypeUrlFragment}-${this?.previewType}`}
              afterCreate={storeNode}
              data-node-ref={"createButton"}
              slot="footer-end"
              scale="l"
              disabled={this.isOverlayShowing === false}
              onclick={this._initPublishOverlay.bind(this)}
            >
              {appPreviewModal.choose}
            </calcite-button>
          }
        </calcite-dialog>
      </div>
    );
  }

  destroy(): void {
    const rootModal = document.getElementById("rootModal");
    rootModal.classList.add("rootModal--closed");
  }

  close(): void {
    this.sendFocusOnClose?.();
    this.destroy();
    this.scheduleRender();
  }

  private _handleModalClose(modal: HTMLCalciteDialogElement) {
    modal.addEventListener("calciteDialogClose", () => {
      this.destroy();
    });
  }

  private _initPublishOverlay() {
    const { publishAppVM, appTypeInfo, resourceInfo, existingApp } = this;
    this.isOverlayShowing = false;

    if (this.publishAppVM?.navbarVM?.isReadOnly) {
      this.publishAppVM.navbarVM.openReadOnlyWarning();
      return;
    }

    if (existingApp == null) {
      // display normal preview publishForm
      this.publishOverlay = new PublishForm({
        ui: "positionedOverlay",
        mode: "newApp",
        publishAppVM,
        portal: this.publishAppVM.portal,
        appTypeInfo,
        resourceInfo,
        feedbackFunction: (msg: string, success: boolean, appId?: string) => {
          this.publishAppVM.myAppsDataVM.performQuery();
        },
        updateFunction: () => {
          this.isOverlayShowing = true;
        },
        sendFocusOnClose: () => {
          setTimeout(() => {
            focusElement.call(this, this.createButton);
          }, 200);
        },
      });
    } else {
      // display based-off-existing-app preview publishForm
      this.publishOverlay = new PublishForm({
        ui: "positionedOverlay",
        mode: "fromExistingApp",
        appTypeInfo,
        existingAppItem: this.existingApp,
        resourceInfo,
        portal: this.publishAppVM.portal,

        publishAppVM,
        feedbackFunction: (msg: string, success: boolean, appId?: string) => {
          this.publishAppVM.myAppsDataVM.performQuery();
        },
        updateFunction: () => {
          this.isOverlayShowing = true;
        },
        sendFocusOnClose: () => {
          setTimeout(() => {
            focusElement.call(this, this.createButton);
          }, 200);
        },
      });
    }
  }

  private _decidePreviewUrl() {
    const { appTypeUrl, appTypeUrlFragment } = this.appTypeInfo;
    const locale = getLocale();
    const appIdStr = this._decideAppId();
    if (this.previewType === "mapPreview") {
      const { resourceItem, resourceType } = this.resourceInfo;
      const resourceStr = this._decideResourceTypeStr(resourceType);
      this._finalUrl = `${appTypeUrl}?${appIdStr}locale=${locale}&${resourceStr}=${resourceItem.id}`;
    } else {
      const env = findEnvironmentByUrl();
      // sample preview
      if (appIdStr !== "") {
        // if app id exists then use the org that the appId is from
        this._finalUrl = `${appTypeUrl}?${appIdStr}locale=${locale}`;
      } else {
        if(window.location.hostname.includes("localhost") || env === "dev" || env === "qa") {
          // local testing case
          this._finalUrl = `https://webapps.maps.arcgis.com${appTypeUrlFragment}?locale=${locale}`;
        } else {
          this._finalUrl = `${window.location.origin}${appTypeUrlFragment}?locale=${locale}`;
        }
      }
    }
  }

  private _decideAppId(): string {
    return this.existingApp != null ? `appid=${this.existingApp.id}&` : "";
  }

  private _decideResourceTypeStr(resourceType: EResourceType): string {
    if (resourceType === EResourceType.Webmap) {
      return "webmap";
    } else if (resourceType === EResourceType.Webscene) {
      return "webscene";
    } else if (resourceType === EResourceType.Group) {
      return "group";
    } else {
      return "resourceType";
    }
  }
}
